/**
 * SearchView Component
 *
 * Main search results page view.
 * Combines search input, tabs, filters, and product grid.
 *
 * IMPORTANT: This component receives ALL search data for ALL tabs in ONE batch.
 * Tab switching is CLIENT-SIDE only - no re-fetching.
 * Results are stored in state and reused across tab switches.
 */

'use client';

import { ProductCard, type ProductCardData } from '@/components/brand';
import { ListingFilters } from '@/components/listing';
import { MoreResultsButton } from '../MoreResultsButton';
import { SearchStoreCard } from '../SearchStoreCard';
import { getAddToCartTrackingId, useAddToCart } from '@/hooks/useAddToCart';
import { useClickOutside } from '@/hooks/useClickOutside';
import { useSearchAutocomplete } from '@/hooks/useSearchAutocomplete';
import { BRAND } from '@/lib/cms/config';
import { getLocaleCode } from '@/lib/algolia/config';
import type { FilterState, ProductHit } from '@/lib/algolia/types';
import { searchPaths, type SearchTab } from '@/lib/paths';
import type { AllSearchData, SearchPageData } from '@/lib/searchData';
import { SEARCH_HITS_PER_PAGE } from '@/lib/search/constants';
import {
  fetchSearchTabLoadMore,
  replaceUrlWithoutNavigation,
  syncSearchPageUrl,
  type ClientTabLoadResult,
} from '@/lib/search/loadMoreClient';
import { getGalletPlpProductCardProps } from '@/lib/product/galletPlpProductCardProps';
import { devError } from '@/lib/utils/devLog';
import clsx from 'clsx';
import { useTranslations } from 'next-intl';
import { useRouter } from '@/lib/i18n/navigation';
import { useParams } from 'next/navigation';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { SearchDropdown } from '../SearchDropdown';
import { SearchInput } from '../SearchInput';
import { SearchNoResults } from '../SearchNoResults';
import { SearchTabs } from '../SearchTabs';
import styles from './SearchView.module.scss';

// Gallet has no eyewear catalogue - exclude it from the "Your Results" tab too.
const SHOW_EYEWEAR = BRAND !== 'gallet';
const ALL_TAB_STORES_PREVIEW = 6;

type ResolvedTabData = {
  products: ProductCardData[];
  hits: ProductHit[];
  totalHits: number;
  facets?: SearchPageData['facets'];
  allFacets?: SearchPageData['allFacets'];
  stores?: SearchPageData['stores'];
};

const TAB_DATA_KEYS = {
  watches: 'watches',
  straps: 'straps',
  eyewear: 'eyewear',
  stores: 'stores',
} as const satisfies Record<string, keyof AllSearchData['tabs']>;

function resolveServerFilteredData(
  activeTab: SearchTab,
  filteredTabData: SearchPageData
): ResolvedTabData {
  if (activeTab === 'stores') {
    return {
      products: [],
      hits: [],
      totalHits: filteredTabData.totalHits,
      facets: filteredTabData.facets,
      stores: filteredTabData.stores,
    };
  }

  return {
    products: filteredTabData.products,
    hits: filteredTabData.hits,
    totalHits: filteredTabData.totalHits,
    facets: filteredTabData.facets,
    allFacets: filteredTabData.allFacets,
    stores: undefined,
  };
}

function resolveAllTabData(tabs: AllSearchData['tabs']): ResolvedTabData {
  const { watches, straps, eyewear, stores: storesData } = tabs;
  const eyewearProducts = SHOW_EYEWEAR ? eyewear.products : [];
  const eyewearHits = SHOW_EYEWEAR ? eyewear.hits : [];
  const eyewearTotal = SHOW_EYEWEAR ? eyewear.totalHits : 0;

  return {
    products: [...watches.products, ...straps.products, ...eyewearProducts],
    hits: [...watches.hits, ...straps.hits, ...eyewearHits],
    totalHits: watches.totalHits + straps.totalHits + eyewearTotal + storesData.totalHits,
    facets: undefined,
    stores: storesData.stores,
  };
}

function resolveUnfilteredTabData(
  activeTab: SearchTab,
  tabs: AllSearchData['tabs']
): ResolvedTabData {
  const tabKey = TAB_DATA_KEYS[activeTab as keyof typeof TAB_DATA_KEYS];
  const tabData = tabKey ? tabs[tabKey] : tabs.watches;

  if (activeTab === 'stores' && tabData.storeFacets?.type) {
    return {
      ...tabData,
      facets: {
        store_type: tabData.storeFacets.type,
      },
    };
  }

  return tabData;
}

function applyClientTabOverride(
  baseData: ResolvedTabData,
  activeTab: SearchTab,
  clientOverride?: ClientTabLoadResult
): ResolvedTabData {
  if (clientOverride && activeTab === 'stores') {
    return {
      ...baseData,
      products: [],
      hits: [],
      totalHits: clientOverride.totalHits,
      stores: clientOverride.stores ?? [],
    };
  }

  if (clientOverride) {
    return {
      ...baseData,
      products: clientOverride.products,
      hits: [],
      totalHits: clientOverride.totalHits,
    };
  }

  return baseData;
}

function searchTabToProductKind(tab: SearchTab): 'watches' | 'straps' | 'eyewear' | undefined {
  if (tab === 'watches' || tab === 'straps' || tab === 'eyewear') {
    return tab;
  }
  return undefined;
}

export interface SearchViewProps {
  /** Initial tab from URL */
  initialTab: SearchTab;
  /** Search query */
  query: string;
  /** ALL search data fetched in ONE batch */
  searchData: AllSearchData;
  /** Filtered data for current tab (when filters are active) */
  filteredTabData?: SearchPageData | null;
  /** Locale string */
  locale: string;
  /** Active filters from URL params (product filters) */
  activeFilters?: Partial<FilterState>;
  /** Store type filter from URL params */
  storeTypeFilter?: string[];
  /** Additional class name */
  className?: string;
}

export function SearchView({
  initialTab,
  query,
  searchData,
  filteredTabData,
  locale,
  activeFilters = {},
  storeTypeFilter: propsStoreTypeFilter,
  className,
}: SearchViewProps) {
  const t = useTranslations('search');
  const tBadges = useTranslations('product.badges');
  const tBadgesRef = useRef(tBadges);
  tBadgesRef.current = tBadges;
  const params = useParams();
  const router = useRouter();
  const lang = params.lang as string;
  const { addToCart, addingProductId } = useAddToCart({ itemListName: 'search' });

  // Active tab is client state for instant, no-refetch switching (the batch
  // searchData already holds every tab).
  const [activeTab, setActiveTab] = useState<SearchTab>(initialTab);
  const [inputValue, setInputValue] = useState(query);
  const dropdownContainerRef = useRef<HTMLDivElement>(null);

  const {
    groupedHits,
    groupedStores,
    isSearching,
    showDropdown,
    performSearch,
    handleClear: handleAutocompleteClear,
    handleFocus,
    closeDropdown,
    resetAutocomplete,
  } = useSearchAutocomplete(lang);

  useEffect(() => {
    setInputValue(query);
    resetAutocomplete();
    setClientTabResults({});
  }, [query, resetAutocomplete]);

  useClickOutside(dropdownContainerRef, () => {
    if (showDropdown) {
      closeDropdown();
    }
  });

  // Local state for filters - when null, use props; when set (even empty), use local
  // This allows us to clear filters on tab switch without page navigation
  const [localFilters, setLocalFilters] = useState<Partial<FilterState> | null>(null);
  const [localStoreTypeFilter, setLocalStoreTypeFilter] = useState<string[] | null>(null);
  const [clientTabResults, setClientTabResults] = useState<
    Partial<Record<SearchTab, ClientTabLoadResult>>
  >({});
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [loadingTab, setLoadingTab] = useState<SearchTab | null>(null);

  // Effective filters - local overrides props when set
  const effectiveFilters = localFilters !== null ? localFilters : activeFilters;
  const effectiveStoreTypeFilter =
    localStoreTypeFilter !== null ? localStoreTypeFilter : propsStoreTypeFilter;

  // Handle search submission - this DOES trigger a new server fetch.
  // Always land on "Your Results": a leftover category tab (e.g. Straps)
  // would hide watches/stores that match the new query.
  const handleSearch = useCallback(
    (newQuery: string) => {
      const nextQuery = newQuery.trim();
      if (!nextQuery) {
        return;
      }

      setLocalFilters({});
      setLocalStoreTypeFilter([]);
      setClientTabResults({});
      setActiveTab('all');
      router.push(searchPaths.all(nextQuery));
    },
    [router]
  );

  const handleQueryChange = useCallback(
    (value: string) => {
      setInputValue(value);
      performSearch(value);
    },
    [performSearch]
  );

  const handleClear = useCallback(() => {
    setInputValue('');
    handleAutocompleteClear();
  }, [handleAutocompleteClear]);

  const handleAutocompleteItemClick = useCallback(
    (hit: ProductHit) => {
      const localeCode = getLocaleCode(lang);
      const productName = hit.name?.[localeCode] || hit.name?.['EN_US'] || hit.objectID;

      setInputValue(productName);
      resetAutocomplete();
      handleSearch(productName);
    },
    [lang, resetAutocomplete, handleSearch]
  );

  // Check if filters are active (using effective filters)
  const hasActiveFilters = useMemo(() => {
    const filterKeys: (keyof FilterState)[] = [
      'collection',
      'size',
      'case',
      'dialColor',
      'type',
      'braceletMaterial',
      'braceletColor',
      'material',
      'color',
      'lug',
      'lugType',
      'buckle',
      'lensColor',
      'frameMaterial',
      'priceMin',
      'priceMax',
    ];
    return filterKeys.some((key) => {
      const value = effectiveFilters[key];
      if (Array.isArray(value)) return value.length > 0;
      if (typeof value === 'number') return true;
      return false;
    });
  }, [effectiveFilters]);

  // Handle tab switch - always client-side, reset filters to initial state
  const handleTabChange = useCallback(
    (tab: SearchTab) => {
      // Clear local filters - resets to unfiltered initial data
      setLocalFilters({});
      setLocalStoreTypeFilter([]);
      setClientTabResults({});

      // Switch tab (client-side, no refetch)
      setActiveTab(tab);

      // Keep the address bar in sync with the tab WITHOUT going through Next's
      // router. Next patches window.history.replaceState and, on a path-segment
      // change, remounts this view - which dropped the setActiveTab above and
      // forced a second click. The native History method updates the URL only,
      // so filters (nuqs reads window.location) and refresh/share still target
      // the right tab, while the tab itself stays driven by local state.
      const queryString = query ? `?q=${encodeURIComponent(query)}` : '';
      replaceUrlWithoutNavigation(`/${lang}/search/${tab}${queryString}`);

      // Scroll to top
      window.scrollTo(0, 0);
    },
    [lang, query]
  );

  // Check if store type filter is active
  const hasStoreTypeFilter = effectiveStoreTypeFilter && effectiveStoreTypeFilter.length > 0;

  // Get current tab data from the batch results OR filtered data if available
  const currentTabData = useMemo(() => {
    const isOnInitialTab = activeTab === initialTab;
    const hasNotSwitchedTabs = localFilters === null && localStoreTypeFilter === null;
    const hasFiltersForCurrentTab = activeTab === 'stores' ? hasStoreTypeFilter : hasActiveFilters;
    const currentPage = effectiveFilters.page || 1;
    const usingServerFilteredData =
      Boolean(filteredTabData) &&
      activeTab !== 'all' &&
      isOnInitialTab &&
      hasNotSwitchedTabs &&
      (hasFiltersForCurrentTab || currentPage > 1);

    let baseData: ResolvedTabData;
    if (usingServerFilteredData && filteredTabData) {
      baseData = resolveServerFilteredData(activeTab, filteredTabData);
    } else if (activeTab === 'all') {
      baseData = resolveAllTabData(searchData.tabs);
    } else {
      baseData = resolveUnfilteredTabData(activeTab, searchData.tabs);
    }

    const clientOverride = activeTab !== 'all' ? clientTabResults[activeTab] : undefined;
    return applyClientTabOverride(baseData, activeTab, clientOverride);
  }, [
    activeTab,
    initialTab,
    searchData.tabs,
    filteredTabData,
    hasActiveFilters,
    hasStoreTypeFilter,
    localFilters,
    localStoreTypeFilter,
    effectiveFilters.page,
    clientTabResults,
  ]);

  const getLoadedPageForTab = useCallback(
    (tab: SearchTab): number => {
      if (clientTabResults[tab]?.page) {
        return clientTabResults[tab]!.page;
      }
      if (tab === activeTab && tab === initialTab) {
        return effectiveFilters.page || 1;
      }
      return 1;
    },
    [activeTab, clientTabResults, effectiveFilters.page, initialTab]
  );

  const handleLoadMore = useCallback(
    async (targetTab: SearchTab, options?: { switchTab?: boolean }) => {
      if (targetTab === 'all' || isLoadingMore) {
        return;
      }

      const nextPage = getLoadedPageForTab(targetTab) + 1;
      setIsLoadingMore(true);
      setLoadingTab(targetTab);

      try {
        const result = await fetchSearchTabLoadMore({
          locale,
          tab: targetTab,
          query,
          page: nextPage,
          filters: effectiveFilters,
          storeTypeFilter: effectiveStoreTypeFilter,
          badgeTranslations: (key) => tBadgesRef.current(key),
        });

        setClientTabResults((prev) => ({ ...prev, [targetTab]: result }));

        if (options?.switchTab) {
          setLocalFilters({});
          setLocalStoreTypeFilter([]);
          setActiveTab(targetTab);
          syncSearchPageUrl(lang, targetTab, query, nextPage, false);
          return;
        }

        syncSearchPageUrl(lang, targetTab, query, nextPage, true);
      } catch (error) {
        devError('[SearchView] Load more failed:', error);
      } finally {
        setIsLoadingMore(false);
        setLoadingTab(null);
      }
    },
    [
      effectiveFilters,
      effectiveStoreTypeFilter,
      getLoadedPageForTab,
      isLoadingMore,
      lang,
      locale,
      query,
    ]
  );

  const { products, hits: _hits, totalHits, stores, facets, allFacets } = currentTabData;

  // Compute effective counts - update current tab's count when filtered
  const effectiveCounts = useMemo(() => {
    const baseCounts = searchData.counts;

    // If using filtered data, update the current tab's count
    const isOnInitialTab = activeTab === initialTab;
    const hasNotSwitchedTabs = localFilters === null && localStoreTypeFilter === null;
    const hasFiltersForCurrentTab = activeTab === 'stores' ? hasStoreTypeFilter : hasActiveFilters;

    if (filteredTabData && isOnInitialTab && hasNotSwitchedTabs && hasFiltersForCurrentTab) {
      return {
        ...baseCounts,
        [activeTab]: filteredTabData.totalHits,
        // Also update "all" count to reflect the filtered tab (only for product tabs)
        all:
          activeTab !== 'stores'
            ? baseCounts.all -
              baseCounts[activeTab as keyof typeof baseCounts] +
              filteredTabData.totalHits
            : baseCounts.all,
      };
    }

    return baseCounts;
  }, [
    searchData.counts,
    filteredTabData,
    activeTab,
    initialTab,
    localFilters,
    localStoreTypeFilter,
    hasActiveFilters,
    hasStoreTypeFilter,
  ]);

  // Alias for backwards compatibility in template. For Gallet, drop eyewear
  // from the counts so "Your Results" doesn't include eyewear products.
  const counts = SHOW_EYEWEAR
    ? effectiveCounts
    : { ...effectiveCounts, all: effectiveCounts.all - effectiveCounts.eyewear, eyewear: 0 };

  // Check for results based on tab type
  const hasProductResults = products.length > 0;
  const hasStoreResults = (stores?.length ?? 0) > 0;

  let hasResults: boolean;
  switch (activeTab) {
    case 'all':
      hasResults = hasProductResults || hasStoreResults;
      break;
    case 'stores':
      hasResults = hasStoreResults;
      break;
    default:
      hasResults = hasProductResults;
  }
  // Show filters for product tabs AND stores tab (not on "all" tab)
  const showProductFilters =
    ['watches', 'straps', 'eyewear'].includes(activeTab) && hasProductResults;
  const showStoreFilters = activeTab === 'stores' && hasStoreResults;

  // Map tab to product label for filters
  const productLabelMap: Record<string, string> = {
    watches: 'watches',
    straps: 'straps',
    eyewear: 'sunglasses',
  };
  const productLabel = productLabelMap[activeTab] || 'watches';

  // Results grid for the active tab ("all" groups by product type)
  const renderResults = () => {
    if (activeTab === 'all') {
      return (
        <AllTabResults
          watchProducts={searchData.tabs.watches.products}
          strapProducts={searchData.tabs.straps.products}
          eyewearProducts={searchData.tabs.eyewear.products}
          stores={searchData.tabs.stores.stores}
          counts={counts}
          locale={locale}
          onAddToBag={addToCart}
          addingProductId={addingProductId}
          onLoadMore={(tab) => handleLoadMore(tab, { switchTab: true })}
          isLoadingMore={isLoadingMore}
          loadingTab={loadingTab}
        />
      );
    }

    if (activeTab === 'stores' && stores && stores.length > 0) {
      return (
        <>
          <div className={styles.storesGrid}>
            {stores.map((store, index) => (
              <SearchStoreCard key={store.objectID} store={store} locale={locale} index={index} />
            ))}
          </div>
          <MoreResultsButton
            displayedCount={stores.length}
            totalCount={totalHits}
            onLoadMore={() => handleLoadMore('stores')}
            isLoading={isLoadingMore && loadingTab === 'stores'}
          />
        </>
      );
    }

    return (
      <>
        <div className={styles.productGrid}>
          {products.map((product) => (
            <ProductCard
              key={product.sku}
              product={product}
              onAddToBag={addToCart}
              isAddingToBag={addingProductId === getAddToCartTrackingId(product)}
              {...getGalletPlpProductCardProps(searchTabToProductKind(activeTab))}
            />
          ))}
        </div>
        <MoreResultsButton
          displayedCount={products.length}
          totalCount={totalHits}
          onLoadMore={() => handleLoadMore(activeTab)}
          isLoading={isLoadingMore && loadingTab === activeTab}
        />
      </>
    );
  };

  return (
    <div className={clsx(styles.container, className)} data-testid="search-view">
      {/* Header - full width background, centered title */}
      <div className={styles.header}>
        <h1 className={styles.title}>{t('results.title')}</h1>

        {/* Search column - label + input (shares its width/alignment with the tabs) */}
        <div className={styles.searchColumn}>
          <span className={styles.searchLabel}>{t('placeholder')}</span>
          <div ref={dropdownContainerRef} className={styles.searchContainer}>
            <SearchInput
              defaultValue={inputValue}
              onChange={handleQueryChange}
              onSubmit={handleSearch}
              onClear={handleClear}
              onFocus={() => handleFocus(inputValue)}
              variant="page"
              size="md"
            />

            {showDropdown && (
              <SearchDropdown
                groupedHits={groupedHits}
                groupedStores={groupedStores}
                query={inputValue}
                onItemClick={handleAutocompleteItemClick}
                onClose={closeDropdown}
                preventItemNavigation
                isLoading={isSearching}
                isVisible={showDropdown}
                className={styles.dropdown}
              />
            )}
          </div>
        </div>
      </div>

      {/* Tabs - CLIENT-SIDE switching, no page navigation */}
      <div className={styles.tabs}>
        <div className={styles.searchColumn}>
          <SearchTabs
            activeTab={activeTab}
            query={query}
            counts={counts}
            onTabChange={handleTabChange}
          />
        </div>
      </div>

      {/* Found banner */}
      {hasResults && activeTab !== 'all' && (
        <div className={styles.foundBanner}>
          <p className={styles.foundText}>{t(`found.${activeTab}`, { count: totalHits })}</p>
        </div>
      )}

      {/* Filter bar (sticky mode - wrapped in container for max-width) - below the found banner */}
      {(showProductFilters || showStoreFilters) && facets && Object.keys(facets).length > 0 && (
        <div className={styles.filtersWrapper}>
          <div className={styles.filtersInner}>
            {showProductFilters && (
              <ListingFilters
                facets={facets}
                activeFilters={effectiveFilters as FilterState}
                totalResults={totalHits}
                position="sticky"
                allFacets={allFacets || facets}
                currentSort={effectiveFilters.sortBy || 'relevance'}
                productLabel={productLabel}
              />
            )}
            {showStoreFilters && (
              <ListingFilters
                facets={facets}
                activeFilters={
                  { ...effectiveFilters, storeType: effectiveStoreTypeFilter } as FilterState
                }
                totalResults={totalHits}
                position="sticky"
                allFacets={facets}
                currentSort="relevance"
                productLabel="stores"
              />
            )}
          </div>
        </div>
      )}

      {/* Content */}
      <div className={styles.content}>
        {/* No Results */}
        {!hasResults && query && <SearchNoResults query={query} />}

        {/* Empty state - no query */}
        {!hasResults && !query && (
          <div className={styles.emptyState}>
            <p className={styles.emptyMessage}>Enter a search term to find products</p>
          </div>
        )}

        {/* Results Grid */}
        {hasResults && renderResults()}
      </div>
    </div>
  );
}

/**
 * All tab results - grouped by product type with preview, plus stores
 */
interface AllTabResultsProps {
  watchProducts: AllSearchData['tabs']['watches']['products'];
  strapProducts: AllSearchData['tabs']['straps']['products'];
  eyewearProducts: AllSearchData['tabs']['eyewear']['products'];
  stores?: AllSearchData['tabs']['stores']['stores'];
  counts: {
    watches: number;
    straps: number;
    eyewear: number;
    stores: number;
  };
  locale: string;
  onAddToBag: (product: ProductCardData) => void;
  addingProductId: string | null;
  onLoadMore: (tab: SearchTab) => void;
  isLoadingMore: boolean;
  loadingTab: SearchTab | null;
}

function AllTabResults({
  watchProducts,
  strapProducts,
  eyewearProducts,
  stores,
  counts,
  locale,
  onAddToBag,
  addingProductId,
  onLoadMore,
  isLoadingMore,
  loadingTab,
}: Readonly<AllTabResultsProps>) {
  const t = useTranslations('search');

  const sections = [
    { type: 'watches' as const, products: watchProducts, count: counts.watches },
    { type: 'straps' as const, products: strapProducts, count: counts.straps },
    ...(SHOW_EYEWEAR
      ? [{ type: 'eyewear' as const, products: eyewearProducts, count: counts.eyewear }]
      : []),
  ];

  return (
    <div className={styles.allTabContent}>
      {sections.map(({ type, products, count }) => {
        if (!products || products.length === 0) return null;

        const previewProducts = products.slice(0, SEARCH_HITS_PER_PAGE);

        return (
          <section key={type} className={styles.productSection}>
            <h2 className={styles.sectionBanner}>{t(`found.${type}`, { count })}</h2>
            <div className={styles.productGrid}>
              {previewProducts.map((product) => (
                <ProductCard
                  key={product.sku}
                  product={product}
                  onAddToBag={onAddToBag}
                  isAddingToBag={addingProductId === getAddToCartTrackingId(product)}
                  {...getGalletPlpProductCardProps(type)}
                />
              ))}
            </div>
            <MoreResultsButton
              displayedCount={previewProducts.length}
              totalCount={count}
              onLoadMore={() => onLoadMore(type)}
              isLoading={isLoadingMore && loadingTab === type}
            />
          </section>
        );
      })}

      {/* Stores section */}
      {stores && stores.length > 0 && (
        <section className={styles.productSection}>
          <h2 className={styles.sectionBanner}>{t('found.stores', { count: counts.stores })}</h2>
          <div className={styles.storesGrid}>
            {stores.slice(0, ALL_TAB_STORES_PREVIEW).map((store, index) => (
              <SearchStoreCard key={store.objectID} store={store} locale={locale} index={index} />
            ))}
          </div>
          <MoreResultsButton
            displayedCount={Math.min(stores.length, ALL_TAB_STORES_PREVIEW)}
            totalCount={counts.stores}
            onLoadMore={() => onLoadMore('stores')}
            isLoading={isLoadingMore && loadingTab === 'stores'}
          />
        </section>
      )}
    </div>
  );
}

SearchView.displayName = 'SearchView';
